// Klänge (Erste Fassung, Runde 5): die Klänge des Sound-Leads aus public/sound/manifest.json, nach seinen Hinweisen dort.
// Teppiche laufen als nahtlose Schleifen und tauschen mit der Sturmstufe die Schichten, Streugeräusche kommen zufällig,
// Donner nach der Sekundenregel, Kampf und Bedienung einmal, Musik je Lage. Drei Regler im Menü: Gesamt, Musik,
// Geräusche. Der Ton startet erst nach der ersten Eingabe (Browser-Regel). Reine Darstellung, liest nur.
import type { BattleResult } from '../sim/index.ts';
import { settings } from './settings.ts';

type Klang = {
  id: string;
  art: 'teppich' | 'streu' | 'kampf' | 'ui' | 'musik';
  dateien: string[];
  schleife?: boolean;
  lautstaerke: number;
  sturmstufen?: number[];
  abstand_s?: number[];
  pan?: [number, number];
  tonhoehe?: [number, number];
  wofuer?: { biom?: string };
};
type Manifest = { version: number; sturmstufen: string[]; klaenge: Klang[] };

/** Wo man gerade ist: Startseite, Fahrt, Kampf am Ufer, Ende eines Runs oder still */
export type SoundScene = {
  mode: 'title' | 'fahrt' | 'kampf' | 'ende' | 'still';
  biome: string;
  stage: string;
  /** B24 (Fabi, 29.10.2026): Klang von drinnen in der Innenansicht immer leise, von außen nur mit der Maus über dem Boot */
  inside?: boolean;
};

const DIR = './sound/';
let manifest: Manifest | null = null;
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let musicBus: GainNode | null = null;
let sfxBus: GainNode | null = null;
/** Teppiche: Regler Geräusche, aber unter dem Ducking wie die Musik */
let ambBus: GainNode | null = null;
/** Teppiche und Musik werden bei Sieg, Niederlage und Kniffen kurz leiser (Hinweis des Sound-Leads) */
let duck: GainNode | null = null;
let scene: SoundScene = { mode: 'still', biome: 'tannenwald', stage: 'ruhig' };
const buffers = new Map<string, AudioBuffer | 'loading' | 'failed'>();
const loops = new Map<string, { src: AudioBufferSourceNode; gain: GainNode; target: number; silentFor: number }>();
const lastFile = new Map<string, string>();
const voices = new Map<string, number>();
const sinceStreu = new Map<string, number>();
let thunder: { next: number; pending: { at: number; id: string } | null } = { next: 8, pending: null };
let clock = 0;
let duckUntil = 0;

const byId = (id: string) => manifest?.klaenge.find((k) => k.id === id);

/** Manifest laden und auf die erste Eingabe warten */
export function initSound(): void {
  if (typeof fetch !== 'function') return;
  fetch(DIR + 'manifest.json')
    .then((r) => (r.ok ? (r.json() as Promise<Manifest>) : null))
    .then((m) => (manifest = m && Array.isArray(m.klaenge) ? m : null))
    .catch(() => (manifest = null));
  const start = () => {
    unlock();
    if (ctx) {
      window.removeEventListener('pointerdown', start);
      window.removeEventListener('keydown', start);
    }
  };
  window.addEventListener('pointerdown', start);
  window.addEventListener('keydown', start);
  // Ist der Tab weg, schweigt das Spiel
  document.addEventListener('visibilitychange', () => {
    if (!ctx) return;
    if (document.hidden) void ctx.suspend();
    else void ctx.resume();
  });
}

function unlock(): void {
  if (ctx) {
    if (ctx.state !== 'running') void ctx.resume();
    return;
  }
  const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return;
  ctx = new AC();
  master = ctx.createGain();
  master.connect(ctx.destination);
  musicBus = ctx.createGain();
  sfxBus = ctx.createGain();
  duck = ctx.createGain();
  ambBus = ctx.createGain();
  duck.connect(master);
  musicBus.connect(duck);
  ambBus.connect(duck);
  sfxBus.connect(master);
  applyVolumes();
}

/** Die Regler im Menü */
export function applyVolumes(): void {
  if (!ctx || !master || !musicBus || !sfxBus || !ambBus) return;
  const s = settings();
  const now = ctx.currentTime;
  master.gain.setTargetAtTime(s.master, now, 0.05);
  musicBus.gain.setTargetAtTime(s.music, now, 0.05);
  sfxBus.gain.setTargetAtTime(s.sounds, now, 0.05);
  ambBus.gain.setTargetAtTime(s.sounds, now, 0.05);
}

function buffer(file: string): AudioBuffer | null {
  const b = buffers.get(file);
  if (b instanceof AudioBuffer) return b;
  if (b || !ctx) return null;
  buffers.set(file, 'loading');
  const c = ctx;
  fetch(DIR + file)
    .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(file))))
    .then((data) => c.decodeAudioData(data))
    .then((buf) => buffers.set(file, buf))
    .catch(() => buffers.set(file, 'failed'));
  return null;
}

export function setSoundScene(s: SoundScene): void {
  scene = s;
}

/** Index der Sturmstufe in der Reihenfolge des Manifests */
const stageIndex = () => Math.max(0, manifest?.sturmstufen.indexOf(scene.stage) ?? 0);
const inBiome = (k: Klang) => !k.wofuer?.biom || k.wofuer.biom === 'alle' || k.wofuer.biom === scene.biome;

/** Wie laut eine Schleife gerade sein soll */
function loopTarget(k: Klang): number {
  const m = scene.mode;
  if (k.art === 'musik') {
    const want = m === 'fahrt' || m === 'title' ? 'musik_fahrt' : m === 'kampf' ? 'musik_kampf' : m === 'ende' ? 'musik_ende' : '';
    return k.id === want ? k.lautstaerke : 0;
  }
  if (k.art !== 'teppich' || m === 'still') return 0;
  if (m === 'title') return ({ fluss: 0.5, wald_tannenwald: 0.6, wind: 0.3 } as Record<string, number>)[k.id] ?? 0;
  if (m === 'ende') return k.id === 'fluss' ? 0.4 : 0;
  if (!inBiome(k)) return 0;
  const gain = (k.sturmstufen?.[stageIndex()] ?? 1) * k.lautstaerke;
  // Am Ufer laufen Wald, Wind und Regen weiter, Fluss und das Innere des Boots leiser
  if (m === 'kampf' && (k.id === 'fluss' || k.id === 'innen')) return gain * 0.3;
  if (m === 'fahrt' && k.id === 'innen' && !scene.inside) return 0;
  return gain;
}

/** Ein Bild: Schleifen nachführen, Streugeräusche und Donner würfeln */
export function soundFrame(dt: number): void {
  if (!ctx || !manifest || ctx.state !== 'running' || !duck) return;
  clock += dt;
  const now = ctx.currentTime;
  duck.gain.setTargetAtTime(clock < duckUntil ? 0.6 : 1, now, 0.15);
  for (const k of manifest.klaenge) {
    if (k.art !== 'teppich' && k.art !== 'musik') continue;
    const target = loopTarget(k);
    let l = loops.get(k.id);
    if (!l && target > 0) {
      const buf = buffer(k.dateien[0]!);
      if (!buf) continue;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      src.connect(gain);
      gain.connect(k.art === 'musik' ? musicBus! : ambBus!);
      // jeder Teppich an zufälliger Stelle (Hinweis des Sound-Leads)
      src.start(0, Math.random() * buf.duration);
      l = { src, gain, target: -1, silentFor: 0 };
      loops.set(k.id, l);
    }
    if (!l) continue;
    if (Math.abs(l.target - target) > 1e-4) {
      // Teppiche blenden in etwa 3 s über, Musik in etwa 2 s
      l.gain.gain.setTargetAtTime(target, now, k.art === 'musik' ? 0.6 : 1);
      l.target = target;
    }
    l.silentFor = target > 0 ? 0 : l.silentFor + dt;
    if (l.silentFor > 12) {
      l.src.stop();
      loops.delete(k.id);
    }
  }
  scatter(dt);
}

/**
 * Streugeräusche (Hinweis des Sound-Leads): frühestens nach 35 % des mittleren Abstands wieder, danach je Sekunde mit
 * Wahrscheinlichkeit 1 / (0,65 × Abstand). Am Ufer kein Knarzen und Plätschern, das gehört zum Boot.
 */
function scatter(dt: number): void {
  if (scene.mode !== 'fahrt' && scene.mode !== 'kampf') return;
  const i = stageIndex();
  for (const k of manifest!.klaenge) {
    if (k.art !== 'streu' || k.id.startsWith('donner') || !inBiome(k)) continue;
    if (scene.mode === 'kampf' && (k.id === 'knarzen' || k.id === 'plaetschern')) continue;
    const gap = k.abstand_s?.[i] ?? 0;
    if (gap <= 0) continue;
    const since = (sinceStreu.get(k.id) ?? Math.random() * gap) + dt;
    sinceStreu.set(k.id, since);
    if (since < 0.35 * gap) continue;
    if (Math.random() < dt / (0.65 * gap)) {
      sinceStreu.set(k.id, 0);
      play(k.id);
    }
  }
  // Donner nach der Sekundenregel (Vorschlag des Sound-Leads): Wetterleuchten ohne Donner, Nah fern und spät, Dicht nah
  // und kurz danach, Eingeholt gleichzeitig mit dem Blitz
  const stage = scene.stage;
  const rule: Record<string, { every: number; id: string; delay: [number, number] } | undefined> = {
    wetterleuchten: { every: 24, id: '', delay: [0, 0] },
    nah: { every: 16, id: 'donner_fern', delay: [3, 6] },
    dicht: { every: 9, id: 'donner_nah', delay: [0.2, 1] },
    eingeholt: { every: 6, id: 'donner_nah', delay: [0, 0] },
  };
  const r = rule[stage];
  if (thunder.pending && clock >= thunder.pending.at) {
    if (thunder.pending.id) play(thunder.pending.id);
    thunder.pending = null;
  }
  if (!r) {
    thunder.next = clock + 4;
    return;
  }
  if (clock >= thunder.next) {
    thunder.pending = { at: clock + r.delay[0] + Math.random() * (r.delay[1] - r.delay[0]), id: r.id };
    thunder.next = clock + r.every * (0.6 + Math.random() * 0.8);
    lightningHook?.();
  }
}

/** Läuft der Ton? Dann blitzt es passend zum Donner */
export const soundRunning = (): boolean => ctx?.state === 'running' && !!manifest;

/** Wer einen Blitz zeigen will, wenn der Donner gewürfelt wird (die Fahrt) */
let lightningHook: (() => void) | null = null;
export function onLightning(f: () => void): void {
  lightningHook = f;
}

/**
 * Einmal abspielen: eine Fassung (nie dieselbe zweimal hintereinander), Geschwindigkeit in tonhoehe, Lautstärke mal 0,8
 * bis 1,0, Stereo-Lage in pan; höchstens vier gleichzeitig von einem Klang
 */
export function play(id: string, gainFactor = 1): void {
  if (!ctx || ctx.state !== 'running' || !sfxBus) return;
  const k = byId(id);
  if (!k || !k.dateien.length) return;
  if ((voices.get(id) ?? 0) >= 4) return;
  const choices = k.dateien.length > 1 ? k.dateien.filter((f) => f !== lastFile.get(id)) : k.dateien;
  const file = choices[Math.floor(Math.random() * choices.length)]!;
  const buf = buffer(file);
  // die anderen Fassungen schon laden
  for (const f of k.dateien) buffer(f);
  if (!buf) return;
  lastFile.set(id, file);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const [lo, hi] = k.tonhoehe ?? [1, 1];
  src.playbackRate.value = lo + Math.random() * (hi - lo);
  const gain = ctx.createGain();
  gain.gain.value = k.lautstaerke * (0.8 + Math.random() * 0.2) * gainFactor;
  let node: AudioNode = gain;
  if (k.pan && typeof ctx.createStereoPanner === 'function') {
    const p = ctx.createStereoPanner();
    p.pan.value = k.pan[0] + Math.random() * (k.pan[1] - k.pan[0]);
    gain.connect(p);
    node = p;
  }
  src.connect(gain);
  node.connect(sfxBus);
  voices.set(id, (voices.get(id) ?? 0) + 1);
  src.onended = () => voices.set(id, Math.max(0, (voices.get(id) ?? 1) - 1));
  src.start();
}

/** Musik und Teppiche kurz leiser, etwa bei Sieg, Niederlage und Kniffen */
export function duckFor(seconds: number): void {
  duckUntil = Math.max(duckUntil, clock + seconds);
}

/** Kampf am Ufer: Klänge der Ereignisse, die seit dem letzten Bild dran waren; beim Spulen keine */
export function battleSounds(r: BattleResult, from: number, to: number): void {
  if (to <= from || to - from > 0.6) return;
  let hits = 0;
  for (const e of r.events) {
    if (e.t <= from || e.t > to) continue;
    if (e.type === 'damage' && e.amount > 0) {
      // höchstens drei, vier Treffer auf einmal, den Rest überspringen
      if (hits++ >= 3) continue;
      play(e.crit ? 'volltreffer' : 'treffer');
    } else if (e.type === 'kniff') {
      const u = r.units[e.unit];
      play(u?.side === 'friend' ? 'kniff' : 'kniff_sturm');
      duckFor(1);
    } else if (e.type === 'down') {
      const u = r.units[e.unit];
      if (u?.kind === 'tree') continue;
      play(u?.side === 'friend' ? 'fallen' : 'vertrieben');
    }
  }
}

/** Ende eines Kampfs: kleine Melodie, Musik kurz leiser */
export function battleEndSound(outcome: BattleResult['outcome']): void {
  play(outcome === 'win' ? 'sieg' : 'niederlage');
  duckFor(2);
}

/** Zum Prüfen im Browser: was läuft gerade */
export function soundDebug(): { running: boolean; scene: SoundScene; loops: Record<string, number>; loaded: number } {
  const out: Record<string, number> = {};
  for (const [id, l] of loops) out[id] = Math.round(l.target * 100) / 100;
  return { running: ctx?.state === 'running', scene, loops: out, loaded: [...buffers.values()].filter((b) => b instanceof AudioBuffer).length };
}
